<template>
    <Breadcrumb :items="[{ name: 'الرئيسية', path: '/' }, { name: 'الإعدادات المالية', path: '/financial-settings' }]" />
    <Loading v-if="loading" />

    <div v-if="!loading">
        <!-- Academic Year Selector -->
        <div class="row px-2 mb-4">
            <div class="col-lg-4 col-md-6 col-12">
                <div class="form-group d-flex flex-column">
                    <label>اختر الفصل الدراسي</label>
                    <Dropdown v-model="selectedYear" :options="academicYears" optionLabel="name"
                        placeholder="اختر فصلاً لإدارة بنوده المالية" @change="loadItems" />
                </div>
            </div>
        </div>

        <template v-if="selectedYear">
            <!-- Add Item -->
            <div class="card mb-4">
                <div class="card-header fw-bold">
                    <i class="pi pi-plus-circle me-1"></i> إضافة بند مالي جديد
                </div>
                <div class="card-body">
                    <div class="row g-3 align-items-start p-3">
                        <div class="col-md-5">
                            <label>اسم البند <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="newItem.name"
                                placeholder="مثال: شهر أغسطس، الترم الأول، رسوم الكتب ..."
                                @keyup.enter="addItem" />
                        </div>
                        <div class="col-md-3">
                            <label>القيمة بالجنيه (اختياري)</label>
                            <input type="number" class="form-control" v-model="newItem.amount"
                                placeholder="500" min="0" step="0.01" />
                        </div>
                        <div class="col-md-2">
                            <label class="non-visible">حفظ</label>
                            <button class="btn PrimaryButton w-100" @click="addItem"
                                :disabled="!newItem.name.trim() || saving">
                                <i class="pi pi-plus me-1"></i>
                                {{ saving ? 'جارٍ الحفظ...' : 'إضافة' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Items List -->
            <div class="card">
                <div class="card-header fw-bold">
                    <i class="pi pi-list me-1"></i>
                    البنود المالية — {{ selectedYear.name }}
                    <span class="badge bg-secondary ms-2">{{ items.length }} بند</span>
                </div>
                <div class="card-body p-0">
                    <div v-if="loadingItems" class="text-center py-4">
                        <i class="pi pi-spin pi-spinner"></i> جارٍ التحميل...
                    </div>
                    <div v-else-if="items.length === 0" class="text-center py-5 text-muted">
                        <i class="pi pi-inbox" style="font-size:2rem;"></i>
                        <p class="mt-2">لا توجد بنود مالية بعد. أضف بنداً من الأعلى.</p>
                    </div>
                    <table v-else class="table table-bordered mb-0">
                        <thead class="table-light">
                            <tr>
                                <th style="width:60px">ترتيب</th>
                                <th>اسم البند</th>
                                <th style="width:130px">القيمة</th>
                                <th style="width:90px">الحالة</th>
                                <th style="width:160px">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in items" :key="item.id"
                                :class="{ 'table-secondary': !item.is_active }">
                                <td>
                                    <div class="d-flex flex-column gap-1">
                                        <button class="btn btn-sm btn-outline-secondary py-0 px-2"
                                            @click="moveUp(idx)" :disabled="idx === 0" title="للأعلى">▲</button>
                                        <button class="btn btn-sm btn-outline-secondary py-0 px-2"
                                            @click="moveDown(idx)" :disabled="idx === items.length - 1"
                                            title="للأسفل">▼</button>
                                    </div>
                                </td>
                                <td>
                                    <template v-if="editingId === item.id">
                                        <input type="text" class="form-control form-control-sm"
                                            v-model="editForm.name" @keyup.enter="saveEdit(item)"
                                            @keyup.escape="editingId = null" />
                                    </template>
                                    <template v-else>
                                        <span :class="{ 'text-muted text-decoration-line-through': !item.is_active }">
                                            {{ item.name }}
                                        </span>
                                    </template>
                                </td>
                                <td>
                                    <template v-if="editingId === item.id">
                                        <input type="number" class="form-control form-control-sm"
                                            v-model="editForm.amount" min="0" placeholder="اختياري" />
                                    </template>
                                    <template v-else>
                                        {{ item.amount ? item.amount + ' ج' : '—' }}
                                    </template>
                                </td>
                                <td>
                                    <span :class="item.is_active ? 'badge bg-success' : 'badge bg-secondary'">
                                        {{ item.is_active ? 'مفعّل' : 'معطّل' }}
                                    </span>
                                </td>
                                <td>
                                    <template v-if="editingId === item.id">
                                        <button class="btn btn-sm btn-success me-1" @click="saveEdit(item)">
                                            <i class="pi pi-check"></i>
                                        </button>
                                        <button class="btn btn-sm btn-secondary" @click="editingId = null">
                                            <i class="pi pi-times"></i>
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button class="btn btn-sm btn-primary" @click="startEdit(item)"
                                            title="تعديل">
                                            <i class="pi pi-pencil"></i>
                                        </button>
                                        <button class="btn btn-sm btn-outline-secondary me-2"
                                            @click="toggleActive(item)"
                                            :title="item.is_active ? 'تعطيل' : 'تفعيل'">
                                            <i :class="item.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                                        </button>
                                        <button class="btn btn-sm btn-danger me-2" @click="deleteItem(item)"
                                            title="حذف">
                                            <i class="pi pi-trash"></i>
                                        </button>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
    name: 'FinancialSettings',
    components: { Dropdown, Breadcrumb, Loading },

    data() {
        return {
            loading: false,
            loadingItems: false,
            saving: false,
            academicYears: [],
            selectedYear: null,
            items: [],
            newItem: { name: '', amount: null },
            editingId: null,
            editForm: { name: '', amount: null },
        };
    },

    methods: {
        async loadAcademicYears() {
            this.loading = true;
            const gradeStore = useGradeStore();
            if (!gradeStore.allGrades.length) {
                await gradeStore.fetchAllGrades();
            }
            this.academicYears = gradeStore.allGrades;
            this.loading = false;
        },

        async loadItems() {
            if (!this.selectedYear) return;
            this.loadingItems = true;
            const res = await axios.get('/financial-items', {
                params: { academic_year_id: this.selectedYear.id },
            });
            this.items = res.data.data ?? [];
            this.loadingItems = false;
        },

        async addItem() {
            if (!this.newItem.name.trim() || this.saving) return;
            this.saving = true;
            try {
                const res = await axios.post('/financial-items', {
                    academic_year_id: this.selectedYear.id,
                    name: this.newItem.name.trim(),
                    amount: this.newItem.amount || null,
                    sort_order: this.items.length,
                });
                this.items.push(res.data.data);
                this.newItem = { name: '', amount: null };
            } finally {
                this.saving = false;
            }
        },

        startEdit(item) {
            this.editingId = item.id;
            this.editForm = { name: item.name, amount: item.amount };
        },

        async saveEdit(item) {
            await axios.put(`/financial-items/${item.id}`, {
                name: this.editForm.name,
                amount: this.editForm.amount || null,
            });
            item.name = this.editForm.name;
            item.amount = this.editForm.amount || null;
            this.editingId = null;
        },

        async toggleActive(item) {
            await axios.put(`/financial-items/${item.id}`, { is_active: !item.is_active });
            item.is_active = !item.is_active;
        },

        async deleteItem(item) {
            const result = await Swal.fire({
                title: 'حذف البند المالي',
                html: `سيتم حذف <strong>${item.name}</strong> وجميع سجلات الدفع المرتبطة به نهائياً.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'نعم، احذف',
                cancelButtonText: 'إلغاء',
                confirmButtonColor: '#dc3545',
            });
            if (!result.isConfirmed) return;
            await axios.delete(`/financial-items/${item.id}`);
            this.items = this.items.filter(i => i.id !== item.id);
        },

        moveUp(idx) {
            if (idx === 0) return;
            const arr = [...this.items];
            [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
            this.items = arr;
            this.saveOrder();
        },

        moveDown(idx) {
            if (idx >= this.items.length - 1) return;
            const arr = [...this.items];
            [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
            this.items = arr;
            this.saveOrder();
        },

        async saveOrder() {
            const payload = this.items.map((item, i) => ({ id: item.id, sort_order: i }));
            await axios.post('/financial-items/reorder', { items: payload });
            this.items.forEach((item, i) => { item.sort_order = i; });
        },
    },

    mounted() {
        this.loadAcademicYears();
    },
};
</script>
